<template>
  <div data-test="orderPayouts" class="my-6">
    <AppFilterNavigation no-preserve class="mb-4">
      <template #default="prop">
        <div class="col-12 col-md-2 px-2 my-2 text-center">
          <AppSelectMultipleDates
            :date-from.sync="filters.dateFrom"
            :date-until.sync="filters.dateUntil"
            data-test="dates"
            ignore-default-date
            clearable
            :date-options="dateOptions"
            :is-filter="!prop.mobile"
            :dense="prop.mobile"
            :label="
              $t(
                'people:ShoppingBasket.OrderBalance.Date.label',
                'Date',
                'Date label in Shopping basket balance page'
              )
            "
          />
        </div>
        <div class="col-12 col-md-2 px-2 my-2 text-center">
          <AppGenericSelect
            v-model="filters.statuses"
            :items="statuses"
            multiple
            :is-filter="!prop.mobile"
            :dense="prop.mobile"
            clearable
            :label="
              $t(
                'people:ShoppingBasket.OrderBalance.Statuses.label',
                'Statuses',
                'Statuses  label in Shopping basket balance page'
              )
            "
            item-value="id"
            item-text="name"
          />
        </div>
        <div class="col-12 col-md-2 px-2 my-2 text-center">
          <AppGenericSelect
            v-model="filters.categories"
            :items="categories"
            multiple
            :is-filter="!prop.mobile"
            :dense="prop.mobile"
            clearable
            :label="
              $t(
                'people:ShoppingBasketPage.TransactionsHistoryTab.filters.category.label',
                'Category'
              )
            "
          />
        </div>
        <div class="col-12 col-md-6 px-2 my-2 text-center">
          <AppSearchBox
            :value.sync="filters.search"
            clearable
            :input-label="
              $t(
                'people:ShoppingBasket.OrderHistory.Search.label',
                'Search by keyword or Order ID',
                'Search label in Shopping basket history page'
              )
            "
          />
        </div>
      </template>
    </AppFilterNavigation>

    <v-card color="primary" class="fipa--header" tile />
    <v-data-table
      :loading="$apollo.queries.payouts.loading"
      :items="payouts"
      :headers="headers"
      :headers-length="99"
      :hide-default-header="$vuetify.breakpoint.smAndDown"
      disable-sort
      :items-per-page="-1"
      hide-default-footer
      class="entsys--table"
    >
      <template #item="{ item, isExpanded, expand }">
        <PayoutHistory
          :item="item"
          :transaction-id="item.id"
          :mutation-loading="loading"
          :is-expanded="isExpanded"
          :expand="expand"
        />
      </template>
      <template #expanded-item="{ item }">
        <ExpandedPayoutHistory
          :item="item"
          :transaction-id="item.id"
          :mutation-loading="loading"
        />
      </template>
    </v-data-table>

    <div class="text-right">
      <AppLoadMoreBtn
        :has-more-results="hasMoreResults"
        :loading="$apollo.queries.payouts.loading"
        @loadMore="loadMore"
      />
    </div>
  </div>
</template>
<script>
import gql from "fraql";
import AppReceiptLink from "@/components/elements/AppReceiptLink.vue";

import AppGenericSelect from "@/components/elements/AppSelects/AppGenericSelect.vue";
import AppSearchBox from "@/components/elements/AppSearchBox.vue";
import AppSelectMultipleDates from "@/components/elements/AppSelects/AppSelectMultipleDates.vue";
import AppFilterNavigation from "@/components/elements/AppFilterNavigation.vue";
import AppLoadMoreBtn from "@/components/elements/AppLoadMoreBtn.vue";
import AppShowEventDetails from "@/components/elements/AppShows/event/AppShowEventDetails.vue";
import PayoutHistory from "./elements/PayoutHistory.vue";
import ExpandedPayoutHistory from "./elements/ExpandedPayoutHistory.vue";
import AppShowPayoutStatus from "@/components/elements/AppShows/order/AppShowPayoutStatus.vue";

const PER_PAGE = 15;

export default {
  name: "PayoutsHistory",
  components: {
    AppGenericSelect,
    AppSelectMultipleDates,
    AppSearchBox,
    AppLoadMoreBtn,
    AppFilterNavigation,
    PayoutHistory,
    ExpandedPayoutHistory
  },
  props: {
    personId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      hasMoreResults: true,
      filters: {
        search: "",
        dateFrom: null,
        dateUntil: null,
        statuses: [],
        categories: []
      },
      loading: false,
      payouts: []
    };
  },
  apollo: {
    payouts: {
      query: gql`
        query payoutsHistory(
          $id: ID!
          $offset: Int
          $limit: Int
          $sortDesc: Boolean
          $sortBy: String
          $keyword: String
          $statuses: [String]
          $categories: [String]
          $createdAtFrom: String
          $createdAtUntil: String
          ) {
          person(id: $id) {
            id
            payouts(
              offset: $offset
              limit: $limit
              sortBy: $sortBy
              keyword: $keyword
              sortDesc: $sortDesc
              statuses: $statuses
              categories: $categories
              createdAtFrom: $createdAtFrom
              createdAtUntil: $createdAtUntil
            ) {
              id
              referenceCode
              receiptsStatus
              status
              processor
              amountCents
              reason
              createdAt
              receipt {
                ${AppReceiptLink.fragments.receipt}
              }
              fromEvent {
                ${AppShowEventDetails.fragments.event}
              }
              ${AppShowPayoutStatus.fragments.payout}
            }
          }
        }
      `,
      variables() {
        return {
          id: this.personId,
          offset: 0,
          limit: PER_PAGE,
          sortBy: "createdAt",
          sortDesc: true,
          keyword: this.filters.search,
          statuses: this.filters.statuses,
          createdAtFrom: this.filters.dateFrom,
          createdAtUntil: this.filters.dateUntil,
          categories: this.filters.categories
        };
      },
      update({ person }) {
        this.hasMoreResults = person.payouts.length >= PER_PAGE;
        return JSON.parse(JSON.stringify(person.payouts ?? [])).filter(Boolean);
      },
      fetchPolicy: "cache-and-network"
    }
  },
  computed: {
    statuses() {
      return [
        { id: "PENDING", name: this.$t("common:status.PENDING") },
        { id: "PAID", name: this.$t("common:status.PAID") },
        { id: "CANCELLED", name: this.$t("common:status.CANCELLED") }
      ];
    },
    defaultDate() {
      return [this.$dayjs().format("YYYY") + "-01-01"];
    },
    headers() {
      return [
        {
          text: this.$t(
            "people:ShoppingBasketPage.TransactionsHistoryTab.table.heading.Date.label",
            "Date"
          ),
          sortable: false
        },
        {
          text: this.$t(
            "people:ShoppingBasketPage.TransactionsHistoryTab.table.heading.Transaction.label",
            "Transaction"
          ),
          sortable: false
        },
        {
          text: this.$t(
            "people:ShoppingBasketPage.TransactionsHistoryTab.table.heading.Details.label",
            "Details"
          ),
          sortable: false
        },
        {
          text: this.$t(
            "people:ShoppingBasketPage.TransactionsHistoryTab.table.heading.Status.label",
            "Status"
          ),
          sortable: false
        },
        {
          text: this.$t(
            "people:ShoppingBasketPage.TransactionsHistoryTab.table.heading.Amount.label",
            "Amount"
          ),
          sortable: false
        },
        /*{
          text: this.$t(
            "people:ShoppingBasketPage.TransactionsHistoryTab.table.heading.Receipt.label",
            "Receipt"
          ),
          sortable: false
        },*/
        { text: "", sortable: false }
      ];
    },
    dateOptions() {
      const year = this.$dayjs().format("YYYY");
      return [
        {
          type: "custom",
          text: this.$dayjs().subtract(1, "months").format("MMMM"),
          dateFrom: this.$dayjs()
            .subtract(1, "months")
            .startOf("month")
            .format("YYYY-MM-DD"),
          dateUntil: this.$dayjs()
            .subtract(1, "months")
            .endOf("month")
            .format("YYYY-MM-DD")
        },
        { type: "wholeYear" },
        {
          type: "custom",
          text: year - 1,
          dateFrom: year - 1 + "-01-01",
          dateUntil: year - 1 + "-12-31"
        }
      ];
    },
    categories() {
      return [
        {
          value: "PAYOUT_OF_RETURNS",
          text: this.$t(
            "people:ShoppingBasketPage.TransactionsHistoryTab.filters.category.PAYOUT_OF_RETURNS.label",
            "Payouts of returns"
          )
        },
        {
          value: "PAYOUT_OF_PRIZE_MONEY",
          text: this.$t(
            "people:ShoppingBasketPage.TransactionsHistoryTab.filters.category.PAYOUT_OF_PRIZE_MONEY.label",
            "Payouts of prize money"
          )
        }
      ];
    }
  },
  methods: {
    loadMore() {
      this.$apollo.queries.payouts
        .fetchMore({
          variables: {
            offset: this.payouts.length
          }
        })
        .then(({ data: { person } }) => {
          this.hasMoreResults = person.payouts.length >= PER_PAGE;

          this.payouts = [
            ...this.payouts,
            ...JSON.parse(JSON.stringify(person.payouts)).filter(Boolean)
          ];
        });
    }
  }
};
</script>
